import React from "react";
import { Link } from "react-router-dom";
import PageHero from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { PillButton } from "@/components/site/Kit";
import TalkFirst from "@/components/site/TalkFirst";
import { useOffers } from "@/components/site/Prices";

const two = (n) => String(n).padStart(2, "0");

// What each offer's button does, and the quieter links beside it.
const DOORS = {
  check: { label: "Start the check", to: "/scorecard" },
  small: { label: "Start the check", to: "/scorecard" },
  audit: { label: "Book the Audit", to: "/book" },
};
const LINKS = {
  small: [["The check tells you if this is your size", "/scorecard"]],
  audit: [
    ["Read a sample Audit", "/audit/sample"],
    ["The Audit, day by day", "/audit"],
  ],
  tool: [["From the proof to the tool", "/audit"]],
};
const NOTES = {
  audit: "About three hours of your team's time.",
  tool: "The exact figure is set in your Audit and held for 30 days. A large job can be more; you know before you decide.",
  care: "Set with the tool's price. More for a large setup.",
};

// How we price. Each line is true of every offer above it.
const RULES = [
  [
    "Fixed before you start",
    "You know the price before any work begins. Nothing is billed by the hour.",
  ],
  [
    "Each one ends in something you hold",
    "A number, a document, a working tool. Never a slide deck.",
  ],
  [
    "You can stop after any step",
    "The check does not oblige you to an Audit, and the Audit does not oblige you to a tool.",
  ],
  [
    "If the answer is no, we say so",
    "A job too small gets a smaller offer. An Audit that says do not build still leaves you the document.",
  ],
  [
    "What we build is yours",
    "It runs on your accounts. The code, the configuration and the access stay with you.",
  ],
];

export default function Pricing() {
  const offers = useOffers();
  return (
    <div data-testid="pricing-page">
      <PageHero
        label="Pricing"
        title="Every price is fixed."
        text="Five steps, each with one price and a list of what you hold at the end. You can stop after any of them."
      />

      <section className="px-3 pt-10 sm:px-5 lg:pt-16">
        <ol className="mx-auto max-w-[1400px] space-y-3">
          {offers.map((o, i) => {
            const main = o.key === "audit";
            const soft = main ? "text-white/55" : "text-black/45";
            return (
              <Reveal
                as="li"
                key={o.key}
                delay={Math.min(i, 3) * 70}
                data-testid={`offer-${o.key}`}
                className={`grid gap-x-8 gap-y-8 rounded-[28px] p-7 sm:p-10 lg:grid-cols-12 ${main ? "bg-black text-white" : "bg-[#f7f7f7]"}`}
              >
                <div className="lg:col-span-4">
                  <span className={`text-[12px] tabular-nums ${soft}`}>
                    {two(i)}
                  </span>
                  <h2 className="nm-title mt-3 text-[34px] leading-[1] sm:text-[48px]">
                    {o.name}
                  </h2>
                  <p className={`mt-3 text-[15px] leading-[1.4] ${soft}`}>
                    {o.whom}
                  </p>
                </div>

                <div className="lg:col-span-4">
                  <p
                    className={`text-[11px] uppercase tracking-[0.08em] ${soft}`}
                  >
                    You hold
                  </p>
                  <ul className="mt-3 space-y-2">
                    {o.holds.map((line) => (
                      <li
                        key={line}
                        className="flex gap-3 text-[16px] leading-[1.35]"
                      >
                        <span
                          className={`mt-[9px] h-1 w-1 shrink-0 rounded-full ${main ? "bg-white/50" : "bg-black/40"}`}
                        />
                        {line}
                      </li>
                    ))}
                  </ul>
                  {NOTES[o.key] && (
                    <p className={`mt-4 text-[14px] leading-[1.4] ${soft}`}>
                      {NOTES[o.key]}
                    </p>
                  )}
                </div>

                <div className="flex flex-col lg:col-span-4 lg:items-end lg:text-right">
                  <p className="nm-display text-[52px] leading-[0.95] tracking-[-0.03em] sm:text-[64px]">
                    {o.price}
                  </p>
                  <p className={`mt-2 text-[15px] ${soft}`}>{o.time}</p>
                  <div className="mt-6 flex flex-wrap items-center gap-3 lg:mt-auto lg:justify-end lg:pt-8">
                    {DOORS[o.key] && (
                      <PillButton
                        to={DOORS[o.key].to}
                        variant={main ? "inverse" : "dark"}
                        size="lg"
                      >
                        {DOORS[o.key].label}
                      </PillButton>
                    )}
                    {main && <TalkFirst location="pricing_audit" />}
                  </div>
                  {LINKS[o.key] && (
                    <p
                      className={`mt-4 flex flex-wrap gap-x-5 gap-y-1 text-[14px] lg:justify-end ${main ? "text-white/70" : "text-black/60"}`}
                    >
                      {LINKS[o.key].map(([label, to]) => (
                        <Link key={label} to={to} className="nm-link">
                          {label}
                        </Link>
                      ))}
                    </p>
                  )}
                </div>
              </Reveal>
            );
          })}
        </ol>
      </section>

      <section className="px-5 pt-20 lg:pt-32" data-testid="pricing-rules">
        <div className="mx-auto max-w-[1400px]">
          <Reveal>
            <h2 className="nm-title max-w-[900px] text-[30px] leading-[1.08] sm:text-[48px]">
              How we price.
            </h2>
          </Reveal>
          <ol className="mt-10 border-t border-black/10 lg:mt-14">
            {RULES.map(([name, text], i) => (
              <Reveal
                as="li"
                key={name}
                delay={i * 70}
                className="grid gap-1 border-b border-black/10 py-7 sm:py-9 lg:grid-cols-12 lg:gap-8"
              >
                <span className="hidden text-[12px] tabular-nums opacity-35 lg:col-span-1 lg:block lg:pt-2">
                  {two(i + 1)}
                </span>
                <p className="text-[22px] leading-[1.15] tracking-[-0.02em] sm:text-[28px] lg:col-span-4">
                  {name}
                </p>
                <p className="text-[18px] leading-[1.35] opacity-50 sm:text-[22px] lg:col-span-7">
                  {text}
                </p>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section className="px-5 pt-20 lg:pt-28">
        <div className="mx-auto flex max-w-[1400px] flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <p className="nm-title text-[30px] leading-[1.05] sm:text-[44px]">
            Not sure which is yours? The check says.
          </p>
          <div className="flex flex-wrap gap-3">
            <PillButton to="/scorecard" size="lg">
              Start the check
            </PillButton>
            <TalkFirst location="pricing_closing" tone="light" />
          </div>
        </div>
      </section>
    </div>
  );
}
